import React from 'react';
import { Button, Col, Form, Input, notification, Row, Select, Space, Typography, Upload, Image, InputNumber } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppSelect } from '../../constants/appSelect';
import { CloseOutlined, ContainerOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createCategory, getAllCategories } from '../../services/category';
import { uploadImageCDN, checkValidUpload } from '../../utils/utility';

const itemName = 'Danh mục';
const { Text, Title } = Typography;

const CreateCategory = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategories'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const parentOptions = React.useMemo(() => {
    const all = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const byId = {};
    const roots = [];
    all.forEach((c) => { byId[c.id] = { ...c, children: [] }; });
    all.forEach((c) => {
      const pid = c.parent_id || 0;
      if (pid && byId[pid]) byId[pid].children.push(byId[c.id]); else roots.push(byId[c.id]);
    });
    const sortRecursive = (node) => {
      node.children.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      node.children.forEach(sortRecursive);
    };
    roots.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    roots.forEach(sortRecursive);
    const res = [{ label: '— Không có —', value: 0 }];
    const dfs = (node, path) => {
      const current = [...path, node.title || ''];
      res.push({ label: current.join(' — '), value: node.id });
      node.children.forEach((ch) => dfs(ch, current));
    };
    roots.forEach((r) => dfs(r, []));
    return res;
  }, [allCategoriesRes]);

  const { mutate: mutateCreate, isPending } = useMutation({
    mutationFn: createCategory,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Thêm mới ${itemName} thành công!` });
        navigate(-1);
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleSubmitForm = (data) => {
    const payload = {
      title: data?.title,
      thumb: data?.thumb || '',
      description: data?.description || '',
      parent_id: Number(data?.parent_id || 0),
      trial_link: data?.trial_link || '',
      group_link: data?.group_link || '',
      type: Number(data?.type || 1),
      display_type: Number(data?.display_type || 1),
      order: Number(data?.order || 1),
      status: data?.status,
    };
    mutateCreate(payload);
  };

  const handleUploadThumb = async (file) => {
    const isValid = checkValidUpload(file);
    if (!isValid) {
      return false;
    }
    const url = await uploadImageCDN(file, () => {}, 'toancohien_category');
    if (url) {
      notification.success({ message: 'Tải lên thành công!' });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ thumb: safeUrl });
    }
    return false;
  };

  return (
    <Form name="create-category" form={form} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Thêm {itemName}</Title>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="title"
            label={<Text>Tiêu đề <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: `Vui lòng nhập tiêu đề ${itemName}` }]}
          >
            <Input placeholder={`Nhập tiêu đề ${itemName}...`} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="parent_id" label={<Text>Danh mục cha</Text>} initialValue={0}>
            <Select
              showSearch
              placeholder="Chọn danh mục cha"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={parentOptions}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="thumb" label={<Text>Ảnh đại diện</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ' }]}> 
            <Input
              placeholder="Nhập URL hoặc tải lên ảnh"
              addonBefore={
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={handleUploadThumb}
                  className="hover:cursor-pointer"
                >
                  <UploadOutlined />
                  Upload
                </Upload>
              }
            />
          </Form.Item>
        </Col>

        {/* Preview image when thumb URL is present */}
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.thumb !== cur.thumb}>
          {({ getFieldValue }) => {
            const url = getFieldValue('thumb');
            return url ? (
              <Col xs={24}>
                <div style={{ marginTop: -8, marginBottom: 12 }}>
                  <Image src={url} alt="preview" height={80} style={{ width: 'auto', height: 80, objectFit: 'cover', borderRadius: 4 }} />
                </div>
              </Col>
            ) : null;
          }}
        </Form.Item>

        <Col xs={24}>
          <Form.Item name="description" label={<Text>Mô tả</Text>}>
            <Input.TextArea placeholder={`Nhập mô tả ${itemName}...`} autoSize={{ minRows: 3, maxRows: 6 }} maxLength={1000} showCount />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="type"
            label={<Text>Loại danh mục <span className="text-red-700">*</span></Text>}
            rules={[
              {
                validator: (_, value) => (Number(value || 0) > 0 ? Promise.resolve() : Promise.reject(new Error('Vui lòng chọn loại danh mục'))),
              },
            ]}
          >
            <Select
              placeholder="Chọn loại danh mục"
              options={[
                { value: 0, label: 'Chọn loại danh mục' },
                { value: 1, label: 'Danh mục bài viết' },
                { value: 2, label: 'Danh mục khóa học' },
              ]}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="display_type"
            label={<Text>Loại hiển thị</Text>}
            initialValue={1}
          >
            <Select
              placeholder="Chọn loại hiển thị"
              options={[
                { value: 1, label: 'Mặc định' },
                { value: 2, label: 'Banner' },
              ]}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="order" label={<Text>Thứ tự</Text>} initialValue={1}>
            <InputNumber min={1} className="w-full" />
          </Form.Item>
        </Col>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type !== cur.type}>
          {({ getFieldValue }) =>
            Number(getFieldValue('type')) === 2 ? (
              <>
                <Col xs={12}>
                  <Form.Item name="trial_link" label={<Text>Đường dẫn học thử</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ' }]}> 
                    <Input placeholder="https://..." />
                  </Form.Item>
                </Col>

                <Col xs={12}>
                  <Form.Item name="group_link" label={<Text>Đường dẫn nhóm học tập</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ' }]}> 
                    <Input placeholder="https://..." />
                  </Form.Item>
                </Col>
              </>
            ) : null
          }
        </Form.Item>

        <Col xs={12}>
          <Form.Item
            name="status"
            label={<Text>Trạng thái <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
            initialValue={1}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CreateCategory;
